import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { ChatSessionsPanel, ChatSessionViewModel } from '../ChatClient/ChatSessionsPanel';
import localforage from 'localforage';
import { useCurrentThreadContext } from '../../contexts/CurrentThreadContext';
import { AgentSelection } from './AgentSelection';
import { ChatMessageModel } from '../../client/SmartAssistantBackendClient';
import { deriveTitle } from '../../utils/chatMessageUtils';

export function HistorySidebar() {
  const { threadId, history, agentSelection, setCurrentThreadSelection } = useCurrentThreadContext();
  const [sessions, setSessions] = useState<ChatSessionViewModel[]>([]);
  const sessionsRef = useRef<ChatSessionViewModel[]>([]);
  const chatImportRef = useRef<HTMLInputElement | null>(null);
  const storageKey = 'smartAssistantChatSessions';
  const legacyStorageKey = 'smartAssistantThreads';

  const createChatId = useCallback(() => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `chat-${Math.random().toString(36).slice(2, 11)}`;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let parsed = await localforage.getItem<any[]>(storageKey);
      if ((!parsed || !Array.isArray(parsed)) && legacyStorageKey) {
        const legacy = await localforage.getItem<any[]>(legacyStorageKey);
        if (legacy && Array.isArray(legacy)) {
          parsed = legacy;
          await localforage.setItem(storageKey, legacy);
        }
      }
      if (!parsed || !Array.isArray(parsed)) return;
      if (cancelled) return;
      const mapped = parsed.map((t: any) => ({
        id: String(t.id),
        title: String(t.title || 'Ohne Titel'),
        manualTitle: t.manualTitle ? String(t.manualTitle) : undefined,
        updatedAt: Number(t.updatedAt || Date.now()),
        messages: Array.isArray(t.messages) ? t.messages.map((m: any) => ChatMessageModel.fromJS(m)) : [],
        clarification: undefined,
        agentSelection: t.agentSelection ?? undefined,
      }));
      sessionsRef.current = mapped;
      setSessions(mapped);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const serializable = sessions.map(t => ({
      ...t,
      messages: t.messages?.map(m => typeof (m as any)?.toJSON === 'function' ? (m as any).toJSON() : m) ?? [],
      clarification: undefined,
    }));
    sessionsRef.current = sessions;
    void localforage.setItem(storageKey, serializable);
  }, [sessions]);

  useEffect(() => {
    if (!threadId) return;
    setSessions(prev => {
      const existing = prev.find(t => t.id === threadId);
      const title = existing?.manualTitle || deriveTitle(history);
      const updated: ChatSessionViewModel = {
        id: threadId,
        title,
        manualTitle: existing?.manualTitle,
        updatedAt: Date.now(),
        messages: history,
        clarification: undefined,
        agentSelection,
      };
      const idx = prev.findIndex(t => t.id === threadId);
      if (idx === -1) return [updated, ...prev];
      const next = [...prev];
      next[idx] = updated;
      return next;
    });
  }, [history, threadId]);

  const visibleSessions = useMemo(() => [...sessions].sort((a, b) => b.updatedAt - a.updatedAt), [sessions]);

  const handleSelect = useCallback((id: string) => {
    const target = sessionsRef.current.find(t => t.id === id);
    if (!target) return;
    setCurrentThreadSelection(target.id, target.messages ?? [], target.agentSelection ?? null);
  }, [setCurrentThreadSelection]);

  const handleRename = useCallback((id: string, newTitle: string) => {
    setSessions(prev => prev.map(t =>
      t.id === id ? { ...t, title: newTitle, manualTitle: newTitle } : t
    ));
  }, []);

  const handleDelete = useCallback((id: string) => {
    setSessions(prev => prev.filter(t => t.id !== id));
    if (threadId === id) {
      setCurrentThreadSelection(null, []);
    }
  }, [setCurrentThreadSelection, threadId]);

  const handleNew = useCallback(() => {
    const id = createChatId();
    setSessions(prev => [{
      id,
      title: 'Neuer Chat',
      updatedAt: Date.now(),
      messages: [],
      clarification: undefined,
      agentSelection,
    }, ...prev]);
    setCurrentThreadSelection(id, [], agentSelection);
  }, [agentSelection, createChatId, setCurrentThreadSelection]);

  const handleImport = useCallback(async (file?: File | null) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const messages = Array.isArray(parsed?.messages) ? parsed.messages : [];
      if (messages.length === 0) return;
      const id = createChatId();
      const title = typeof parsed?.title === 'string' && parsed.title.trim()
        ? parsed.title.trim()
        : `Import ${new Date().toLocaleString()}`;
      const normalizedMessages = messages.map((m: any) => ChatMessageModel.fromJS(m));
      const updated: ChatSessionViewModel = {
        id,
        title,
        updatedAt: Date.now(),
        messages: normalizedMessages,
        clarification: undefined,
        agentSelection: parsed?.agentSelection ?? undefined,
      };
      setSessions(prev => [updated, ...prev]);
      setCurrentThreadSelection(id, normalizedMessages, parsed?.agentSelection ?? null);
    } catch (err) {
      console.error('Chat import failed', err);
    }
  }, [createChatId, setCurrentThreadSelection]);

  const selectedHistoryId = threadId ?? null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', pr: 1 }}>
      <input
        ref={chatImportRef}
        type="file"
        accept="application/json"
        style={{ display: 'none' }}
        onChange={(ev) => {
          const file = ev.target.files?.[0];
          ev.target.value = '';
          void handleImport(file);
        }}
      />
      <AgentSelection />
      <ChatSessionsPanel
        sessions={visibleSessions}
        selectedId={selectedHistoryId}
        onSelect={handleSelect}
        onDelete={handleDelete}
        onRename={handleRename}
        onNew={handleNew}
        onImport={() => chatImportRef.current?.click()}
        disabled={false}
      />
    </Box>
  );
}
